const SIGN_UP_NOT_AVAILABLE_MESSAGE = "Signup is not available for this account type";

// The PreSignUp Lambda embeds an extensible hex bitmap of the account's usable
// sign-in factors alongside the identifier. Decode defensively so future bits
// don't break the parse.
const CAPABILITY_BIT = {
  usablePassword: 0x01,
  emailVerified: 0x02,
} as const;

interface ErrorMessageParams {
  errorMessage: string;
}

export function isSignUpNotAvailableErrorMessage({ errorMessage }: ErrorMessageParams): boolean {
  return errorMessage.includes(SIGN_UP_NOT_AVAILABLE_MESSAGE);
}

export function parseCognitoUsernameFromSignUpErrorMessage({
  errorMessage,
}: ErrorMessageParams): string | undefined {
  if (!isSignUpNotAvailableErrorMessage({ errorMessage })) {
    return undefined;
  }

  return /Identifier:\s*"([^"]+)"/.exec(errorMessage)?.[1];
}

/**
 * Decodes the `Capabilities: "0x.."` hex bitmap from the PreSignUp rejection.
 * Returns undefined when the field is absent or malformed so callers can keep
 * older Lambda responses distinct from an explicit `0x00` bitmap.
 */
export function parseCapabilitiesFromSignUpErrorMessage({
  errorMessage,
}: ErrorMessageParams): number | undefined {
  if (!isSignUpNotAvailableErrorMessage({ errorMessage })) {
    return undefined;
  }

  const hex = /Capabilities:\s*"(?:0x)?([0-9a-fA-F]+)"/.exec(errorMessage)?.[1];
  if (!hex) {
    return undefined;
  }

  const parsed = Number.parseInt(hex, 16);
  return Number.isNaN(parsed) ? undefined : parsed;
}

export function capabilitiesIncludeUsablePassword(capabilities: number): boolean {
  // eslint-disable-next-line no-bitwise -- decoding a bitmap flag
  return (capabilities & CAPABILITY_BIT.usablePassword) !== 0;
}

export function capabilitiesIncludeEmailVerified(capabilities: number): boolean {
  // eslint-disable-next-line no-bitwise -- decoding a bitmap flag
  return (capabilities & CAPABILITY_BIT.emailVerified) !== 0;
}
